import React, { useEffect, useState } from 'react';
import { ToggleSwitch } from '../..';
import { submitFormOnEnter } from '../../../../utils';
import { Button, ButtonColor, ButtonSize } from '../../buttons';
import { GenericDropdown } from '../../ui-widgets/generic-dropdown/generic-dropdown';
import { InputWrapper } from '../../ui-inputs/input-wrapper';
import { InputTypes } from '../../ui-inputs/validated-input';

import './form-filters.scss';

export interface FormFilterField {
    key: string;
    label: string;
    type: FormFiltersInputTypes;
    placeholder?: string;
    defaultValue: any;
    options?: { label: string; value: any }[];
}

export interface FormFiltersProps {
    fields: FormFilterField[];
    isDisabled?: boolean;
    onChange?: (filters: Record<string, any>) => void;
}

export enum FormFiltersInputTypes {
    TEXT_INPUT = 'text-input',
    NUMBER_INPUT = 'number-input',
    DROPDOWN = 'dropdown',
    TOGGLE = 'toggle',
}

interface ActiveFilter {
    key: string;
    value: any;
}

export const FormFilters: React.FC<FormFiltersProps> = ({ fields, isDisabled, onChange }): React.ReactElement => {
    const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
    const availableFields = fields.filter((field) => !activeFilters.some((filter) => filter.key === field.key));

    const handleAddFilter = () => {
        if (availableFields.length > 0) {
            const newFilter = {
                key: availableFields[0].key,
                value: availableFields[0].defaultValue,
            };
            setActiveFilters([...activeFilters, newFilter]);
        }
    };

    const handleFieldChange = (key: string, value: any) => {
        const updatedFilters = activeFilters.map((filter) =>
            filter.key === key
                ? {
                      ...filter,
                      value,
                  }
                : filter,
        );
        setActiveFilters(updatedFilters);
    };

    const handleFilterTypeChange = (index: number, newKey: string) => {
        const updatedFilters = [...activeFilters];
        updatedFilters[index] = {
            key: newKey,
            value: fields.find((item) => item.key == newKey).defaultValue,
        };
        setActiveFilters(updatedFilters);
    };

    const handleDeleteFilter = (key: string) => {
        const updatedFilters = activeFilters.filter((filter) => filter.key !== key);
        setActiveFilters(updatedFilters);
        if (updatedFilters.length === 0) {
            applyFilters();
        }
    };

    const applyFilters = () => {
        const filterValues = activeFilters.reduce(
            (acc, filter) => ({
                ...acc,
                [filter.key]: filter.value,
            }),
            {},
        );
        onChange && onChange(filterValues);
    };

    useEffect(() => {
        if (activeFilters.length === 0) {
            applyFilters();
        }
    }, [activeFilters]);

    return (
        <div className="form-filters">
            <div className="form-filters__filters">
                {activeFilters.map((filter, index) => {
                    const field = fields.find((f) => f.key === filter.key);
                    return (
                        <div key={filter.key} className="form-filters__row">
                            <div className="form-filters__filter-type">
                                <GenericDropdown
                                    className="form-filters__filter-type-dropdown"
                                    label={field.label}
                                    onChange={(value: string | number) =>
                                        handleFilterTypeChange(index, value as string)
                                    }
                                    selected={field.key}
                                    isDisabled={isDisabled}
                                    options={fields
                                        .filter(
                                            (f) =>
                                                !activeFilters.some(
                                                    (activeFilter) =>
                                                        activeFilter.key === f.key && f.key !== filter.key,
                                                ),
                                        )
                                        .map((item) => {
                                            return {
                                                label: item.label,
                                                value: item.key,
                                            };
                                        })}
                                />
                            </div>
                            <div className="form-filters__filter-value">
                                {field?.type === FormFiltersInputTypes.TEXT_INPUT && (
                                    <InputWrapper
                                        inputId={field.key}
                                        labelText={field.label}
                                        inputProps={{
                                            type: InputTypes.TEXT,
                                            placeholder: field.placeholder,
                                            value: filter.value,
                                            onChange: (e) => handleFieldChange(filter.key, e.target.value),
                                            onKeyDown: (event) => submitFormOnEnter(event, applyFilters),
                                            disabled: isDisabled,
                                        }}
                                    />
                                )}
                                {field?.type === FormFiltersInputTypes.NUMBER_INPUT && (
                                    <InputWrapper
                                        inputId={field.key}
                                        labelText={field.label}
                                        inputProps={{
                                            type: InputTypes.NUMBER,
                                            placeholder: field.placeholder,
                                            value: filter.value,
                                            onChange: (e) => handleFieldChange(filter.key, Number(e.target.value)),
                                            onKeyDown: (event) => submitFormOnEnter(event, applyFilters),
                                            disabled: isDisabled,
                                        }}
                                    />
                                )}
                                {field?.type === FormFiltersInputTypes.DROPDOWN && (
                                    <GenericDropdown
                                        label={field.label}
                                        onChange={(value: string | number) => handleFieldChange(filter.key, value)}
                                        options={field.options}
                                        isDisabled={isDisabled}
                                    />
                                )}
                                {field?.type === FormFiltersInputTypes.TOGGLE && (
                                    <ToggleSwitch
                                        id={`toggle-${filter.key}`}
                                        active={filter.value}
                                        onChange={() => handleFieldChange(filter.key, !filter.value)}
                                        isDisabled={isDisabled}
                                    />
                                )}
                            </div>
                            <div className="form-filters__filter-remove">
                                <Button
                                    size={ButtonSize.MINI}
                                    onClick={() => handleDeleteFilter(filter.key)}
                                    icon="fas fa-close"
                                    isDisabled={isDisabled}
                                />
                            </div>
                        </div>
                    );
                })}
                <Button
                    color={ButtonColor.PRIMARY}
                    size={ButtonSize.SMALL}
                    className="form-filters__add"
                    onClick={handleAddFilter}
                    isDisabled={isDisabled || availableFields.length === 0}
                    icon="fas fa-plus"
                >
                    Add Search Criteria
                </Button>
            </div>
            {activeFilters.length > 0 && (
                <div className="form-filters__search">
                    <Button
                        color={ButtonColor.PRIMARY}
                        size={ButtonSize.MINI}
                        onClick={applyFilters}
                        icon="fas fa-search"
                        isDisabled={isDisabled}
                    >
                        Search
                    </Button>
                </div>
            )}
        </div>
    );
};
